import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';
import { readCachedSettings } from '../../core/contexts/settingsContextUtils';
import { formatDZD } from './currency';

// Re-export isSaudiBrand for convenience
export { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';

export const SAUDI_CURRENCY_CODE = SAUDI_BRAND.currency || 'SAR';
export const SAUDI_CURRENCY_SYMBOL = 'ر.س';
export const SAUDI_CURRENCY_LABEL_AR = 'ريال سعودي';
export const SAUDI_CURRENCY_LABEL = `${SAUDI_CURRENCY_LABEL_AR} (${SAUDI_CURRENCY_CODE})`;

function resolveSettingsSnapshot(settings) {
  if (settings && typeof settings === 'object') return settings;
  return readCachedSettings();
}

function brandFallbackVatRate() {
  return isSaudiBrand() ? SAUDI_BRAND.vatRate : 19;
}

/**
 * Live VAT % — prefers settings.taxes.tva / module defaultTaxRate, then brand fallback.
 * Pass `settings` from useSettings() in React; otherwise reads qawaem-settings cache.
 */
export function resolveLiveVatRate(settings, moduleConfig) {
  const snap = resolveSettingsSnapshot(settings);
  const fromTaxes = snap?.taxes?.tva;
  if (Number.isFinite(fromTaxes)) return fromTaxes;
  const fromModule = moduleConfig?.defaultTaxRate ?? moduleConfig?.defaultVatRate;
  if (Number.isFinite(fromModule)) return fromModule;
  const fromFacturation = snap?.facturation?.defaultTaxRate;
  if (Number.isFinite(fromFacturation)) return fromFacturation;
  return brandFallbackVatRate();
}

/** Alias — general settings.taxes.tva wins over module defaultTaxRate. */
export const getLiveVatRate = resolveLiveVatRate;

/** VAT rate for the active brand — settings-aware when cache/context is available. */
export function getBrandVatRate(settings) {
  return resolveLiveVatRate(settings);
}

/** Multiplier to derive VAT-inclusive totals from ex-VAT amounts, e.g. 1.15 for 15% VAT. */
export function getBrandVatMultiplier(settings) {
  return 1 + getBrandVatRate(settings) / 100;
}

/** Extract ex-VAT amount from a VAT-inclusive total. */
export function amountExclVat(ttcAmount) {
  const n = typeof ttcAmount === 'number' ? ttcAmount : parseFloat(ttcAmount) || 0;
  return n / getBrandVatMultiplier();
}

/** VAT portion from a VAT-inclusive total. */
export function vatFromTtc(ttcAmount) {
  const n = typeof ttcAmount === 'number' ? ttcAmount : parseFloat(ttcAmount) || 0;
  return n - amountExclVat(n);
}

/** Default currency — prefers settings.regional.currency / invoicing.defaultCurrency. */
export function getBrandDefaultCurrency(settings) {
  const snap = resolveSettingsSnapshot(settings);
  const fromRegional = snap?.regional?.currency;
  if (fromRegional) return normalizeBrandCurrency(fromRegional);
  const fromInvoicing = snap?.invoicing?.defaultCurrency;
  if (fromInvoicing) return normalizeBrandCurrency(fromInvoicing);
  if (snap?.currency) return normalizeBrandCurrency(snap.currency);
  return isSaudiBrand() ? SAUDI_CURRENCY_CODE : 'DZD';
}

/** Qawaem / Saudi builds only support SAR. */
export function isBrandCurrencyLocked() {
  return isSaudiBrand();
}

/** Normalize persisted currency to the brand default when locked. */
export function normalizeBrandCurrency(value) {
  if (isBrandCurrencyLocked()) return SAUDI_CURRENCY_CODE;
  return value || getBrandDefaultCurrency();
}

/** Qawaem / Saudi builds — SAR only. */
export const SAUDI_CURRENCY_OPTIONS = [
  { value: SAUDI_CURRENCY_CODE, label: SAUDI_CURRENCY_LABEL },
];

/** Legacy Algeria options — internal; never use directly when isSaudiBrand(). */
const LEGACY_CURRENCY_OPTIONS = [
  { value: 'DZD', label: 'DZD — Dinar algérien' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'USD', label: 'USD — Dollar US' },
];

export function getBrandCurrencyOptions() {
  if (isSaudiBrand()) {
    return SAUDI_CURRENCY_OPTIONS;
  }
  return LEGACY_CURRENCY_OPTIONS;
}

/** Filter backend/API currency rows — SAR-only when Qawaem/Saudi brand is active. */
export function filterBrandCurrencyRows(rows) {
  if (!Array.isArray(rows)) return [];
  if (!isSaudiBrand()) return rows;
  const filtered = rows.filter((row) => {
    const code = String(row?.currency_code || row?.code || row?.value || '').toUpperCase();
    return code === SAUDI_CURRENCY_CODE;
  });
  return filtered.length > 0 ? filtered : SAUDI_CURRENCY_OPTIONS.map((opt) => ({
    currency_code: opt.value,
    currency_name: opt.label,
    rate: 1,
    is_base: 1,
    volume: 0,
  }));
}

/**
 * Format monetary amounts for the active brand.
 * Saudi: ar-SA locale + ر.س suffix.
 */
export function formatBrandMoney(amount, showSymbol = true, settings) {
  const n = typeof amount === 'number' ? amount : parseFloat(amount) || 0;

  if (isSaudiBrand()) {
    const formatted = new Intl.NumberFormat('ar-SA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
    const symbol = getBrandCurrencySuffix(settings);
    return showSymbol ? `${formatted} ${symbol}` : formatted;
  }

  return formatDZD(n, showSymbol);
}

/** Compact suffix for tables/stats, e.g. "ر.س" or "DA". */
export function getBrandCurrencySuffix(settings) {
  const snap = resolveSettingsSnapshot(settings);
  const sym = snap?.regional?.currencySymbol;
  if (sym && isSaudiBrand()) return sym;
  return isSaudiBrand() ? SAUDI_CURRENCY_SYMBOL : 'DA';
}

/** Compact monetary stat, e.g. "125K ر.س". */
export function formatBrandMoneyCompact(amount) {
  const suffix = getBrandCurrencySuffix();
  const n = Math.abs(Number(amount) || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ${suffix}`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K ${suffix}`;
  return formatBrandMoney(amount);
}

/** BCP-47 locale for dates/numbers in list views and exports. */
export function getBrandLocale(language) {
  if (isSaudiBrand() || language?.startsWith('ar')) return 'ar-SA';
  return 'fr-FR';
}

/** react-datepicker locale id (`ar` or `fr`). */
export function getBrandDatePickerLocale() {
  return isSaudiBrand() ? 'ar' : 'fr';
}

/** Catalog price-tier breakpoints (SAR vs legacy DZD). */
export function getBrandPriceTierThresholds() {
  if (isSaudiBrand()) {
    return { lowMax: 1000, highMin: 10000 };
  }
  return { lowMax: 10000, highMin: 50000 };
}
